import {
  addDays as addDaysFns,
  differenceInCalendarDays,
  format,
  startOfDay,
} from 'date-fns';
import { isWithinInterval } from 'date-fns/isWithinInterval';
import { parseISO } from 'date-fns/parseISO';
import { subDays as subDaysFns } from 'date-fns/subDays';

/**
 * The today's date.
 * @returns The today's date. eg new Date(2025, 0, 1)
 */
export const today = startOfDay(new Date());

/**
 * Checks if the date falls within the last N calendar-aware days from now.
 * Uses date-fns `subDays` so DST transitions are handled correctly.
 * @param date - The date to check. Date object. eg new Date()
 * @param days - Number of days for the lookback window. eg 5
 * @returns True if the date falls within that window, false otherwise.
 */
export function isWithinLastDays(date: Date, days: number) {
  const nowMinusNDays = subDaysFns(new Date(), days);
  return isWithinInterval(date, {
    start: nowMinusNDays,
    end: new Date(),
  });
}

/**
 * Converts the date to a Date object.
 * @param date - ISO string or Date object. eg 2025-01-01T00:00:00.000Z or new Date(2025, 0, 1)
 * @returns The Date object. eg new Date(2025, 0, 1)
 */
export function toDate(date: string | Date | null) {
  return date ? (date instanceof Date ? date : parseISO(date)) : null;
}

/**
 * Formats the date to the short date format.
 * @param date - ISO string or Date object. eg 2025-01-01T00:00:00.000Z or new Date(2025, 0, 1)
 * @returns The formatted date. eg Jan 1, 2025
 */
export function formatShortDate(date: Date | string): string {
  return format(date, 'LLL dd, y');
}

/**
 * Calculates the duration in days between two dates without considering the time.
 * This is inclusive of both start and end dates (e.g., Jan 30 to Feb 28 = 30 days).
 * @param startDate - The start date. ISO string or Date object. eg 2025-01-01T00:00:00.000Z or new Date(2025, 0, 1)
 * @param endDate - The end date. ISO string or Date object. eg 2025-01-01T00:00:00.000Z or new Date(2025, 0, 1)
 * @returns The duration in days (inclusive). eg 30
 */
export function durationInDays(
  startDate: string | Date,
  endDate: string | Date,
) {
  return differenceInCalendarDays(endDate, startDate) + 1;
}

/**
 * Adds the specified number of days to the date. This is inclusive of the start date.
 * For example, adding 30 days to a date will return the date 30 days from the start date including the start date. If you want to add 30 days excluding the start date, you should add 29 days. Hence the -1.
 * @param date - The date to add the days to. ISO string or Date object. eg 2025-01-01T00:00:00.000Z or new Date(2025, 0, 1)
 * @param days - The number of days to add. eg 30
 * @returns The date after adding the days. eg new Date(2025, 0, 31)
 */
export function addDays(date: Date | string, days: number) {
  return addDaysFns(date, Math.max(days - 1, 0));
}
